import React from 'react'

import { CardNoise, DataCard, DataRow } from './styled'
import styled from 'styled-components/macro'
import { Box } from 'rebass/styled-components'
import { RowBetween } from 'components/Row'
import { HRDark } from 'components/HR/HR'
import { TYPE } from '../../theme'
import { AutoColumn } from 'components/Column'
import { CurrencyLogoFromList } from 'components/CurrencyLogo/CurrencyLogoFromList'
import { CurrencyAmount, Token } from 'sdk-core/entities'
import JSBI from 'jsbi'
import { BIG_INT_SECONDS_IN_WEEK, BIG_INT_SECONDS_IN_DAY } from 'constants/misc'
import { CountUp } from 'use-count-up'
import usePrevious from '../../hooks/usePrevious'

interface FarmYieldProps {
  totalDeposits?: CurrencyAmount<Token>
  totalDepositsInUSD?: CurrencyAmount<Token> | null | undefined
  yourDeposits?: CurrencyAmount<Token>
  yourDepositsInUSD?: CurrencyAmount<Token> | null | undefined
  primaryEmissionPerSecond?: CurrencyAmount<Token> | null | undefined
  secondaryEmissionPerSecond?: CurrencyAmount<Token>
  emissionTimeframe?: 'weekly' | 'daily'
  apr?: JSBI
  apy?: JSBI
}

export function FarmYield({
  totalDeposits,
  totalDepositsInUSD,
  yourDeposits,
  yourDepositsInUSD,
  primaryEmissionPerSecond,
  secondaryEmissionPerSecond,
  apr,
  apy,
  emissionTimeframe = 'weekly',
}: FarmYieldProps) {
  const multiplier = emissionTimeframe === 'weekly' ? BIG_INT_SECONDS_IN_WEEK : BIG_INT_SECONDS_IN_DAY

  const countUpTotalDeposits = totalDeposits?.toFixed(6) ?? '0'
  const countUpTotalDepositsPrevious = usePrevious(countUpTotalDeposits) ?? '0'

  const countUpTotalDepositsUSD = totalDepositsInUSD?.toFixed(6) ?? '0'
  const countUpTotalDepositsUSDPrevious = usePrevious(countUpTotalDepositsUSD) ?? '0'

  const countUpYourDeposits = yourDeposits?.toFixed(6) ?? '0'
  const countUpYourDepositsPrevious = usePrevious(countUpYourDeposits) ?? '0'

  const countUpYourDepositsUSD = yourDepositsInUSD?.toFixed(6) ?? '0'
  const countUpYourDepositsUSDPrevious = usePrevious(countUpYourDepositsUSD) ?? '0'

  const countUpAPR = apr ? apr.toString() : '0'
  const countUpAPRPrevious = usePrevious(countUpAPR) ?? '0'

  const primaryEmissionPerSecondCountUp = primaryEmissionPerSecond
    ?.multiply(multiplier)
    ?.toFixed(0, { groupSeparator: '' })
  const primaryEmissionPerSecondCountUpPrevious = usePrevious(primaryEmissionPerSecondCountUp) ?? '0'
  return (
    <DataRow style={{ gap: '24px' }}>
      <PoolData>
        <RowBetween>
          <PoolHeading width={1 / 3} align="end">
            <TYPE.mediumHeader color={'#fff'}>Total deposits</TYPE.mediumHeader>
          </PoolHeading>
          <PoolHeading width={1 / 3} align="end">
            <TYPE.mediumHeader color={'#fff'}>Your Deposit</TYPE.mediumHeader>
          </PoolHeading>
          <PoolHeading width={1 / 3} align="end">
            {apr ? <TYPE.mediumHeader color={'#fff'}>APR</TYPE.mediumHeader> : <></>}
            {apy ? <TYPE.mediumHeader color={'#fff'}>APY</TYPE.mediumHeader> : <></>}
          </PoolHeading>
        </RowBetween>
        <HRDark />
        <RowBetween>
          <PoolHeading width={1 / 3} align="right">
            {totalDeposits ? (
              <TYPE.body fontSize={20} fontWeight={500}>
                {totalDeposits ? (
                  <CountUp
                    key={totalDeposits.toFixed(0)}
                    isCounting
                    decimalPlaces={2}
                    start={parseFloat(countUpTotalDepositsPrevious)}
                    end={parseFloat(countUpTotalDeposits)}
                    thousandsSeparator={','}
                    duration={1}
                  />
                ) : (
                  <></>
                )}
              </TYPE.body>
            ) : (
              <></>
            )}
            <TYPE.body fontSize={!totalDeposits ? 20 : 16} fontWeight={500} color={totalDeposits ? '#fff' : '#fff'}>
              <span>$</span>
              {totalDepositsInUSD ? (
                <CountUp
                  key={totalDepositsInUSD.toFixed(0)}
                  isCounting
                  decimalPlaces={2}
                  start={parseFloat(countUpTotalDepositsUSDPrevious)}
                  end={parseFloat(countUpTotalDepositsUSD)}
                  thousandsSeparator={','}
                  duration={1}
                />
              ) : (
                <></>
              )}
            </TYPE.body>
          </PoolHeading>
          <PoolHeading width={1 / 3} align="right">
            <TYPE.body fontSize={20} fontWeight={500}>
              {yourDeposits ? (
                <CountUp
                  key={yourDeposits.toFixed(0)}
                  isCounting
                  decimalPlaces={2}
                  start={parseFloat(countUpYourDepositsPrevious)}
                  end={parseFloat(countUpYourDeposits)}
                  thousandsSeparator={','}
                  duration={1}
                />
              ) : (
                <></>
              )}
            </TYPE.body>
            <TYPE.body fontSize={!totalDeposits ? 20 : 16} fontWeight={500} color={totalDeposits ? '#fff' : '#fff'}>
              <span>$</span>
              {yourDepositsInUSD ? (
                <CountUp
                  key={yourDepositsInUSD.toFixed(0)}
                  isCounting
                  decimalPlaces={2}
                  start={parseFloat(countUpYourDepositsUSDPrevious)}
                  end={parseFloat(countUpYourDepositsUSD)}
                  thousandsSeparator={','}
                  duration={1}
                />
              ) : (
                <></>
              )}
            </TYPE.body>
          </PoolHeading>
          <PoolHeading width={1 / 3} align="end">
            {apr ? (
              <TYPE.body fontSize={20} fontWeight={500}>
                {JSBI.GT(apr, JSBI.BigInt(0)) ? (
                  <CountUp
                    key={apr.toString()}
                    isCounting
                    decimalPlaces={0}
                    start={parseFloat(countUpAPRPrevious)}
                    end={parseFloat(countUpAPR)}
                    thousandsSeparator={','}
                    duration={1}
                  />
                ) : (
                  <></>
                )}
                <span>%</span>
              </TYPE.body>
            ) : (
              <></>
            )}
            {apy ? (
              <TYPE.body fontSize={20} fontWeight={500}>
                {JSBI.GT(apy, JSBI.BigInt(0)) ? `${apy ? apy.toString() : '0'}%` : '-'}
              </TYPE.body>
            ) : (
              <></>
            )}
          </PoolHeading>
        </RowBetween>
        <CardNoise />
        <RowBetween>
          <RewardRate width={1 / 1} align="center">
            <AutoColumn justify={'stretch'}>
              <Heading>
                <CurrencyLogoFromList currency={primaryEmissionPerSecond?.currency ?? undefined} size={'24px'} />
                <TYPE.body fontWeight={500} margin={'5px'}>
                  {/*{primaryEmissionPerSecond?.multiply(multiplier)?.toFixed(0, { groupSeparator: ',' }) ?? '-'}*/}

                  {primaryEmissionPerSecondCountUp ? (
                    <CountUp
                      key={primaryEmissionPerSecondCountUp}
                      isCounting
                      decimalPlaces={0}
                      start={parseFloat(primaryEmissionPerSecondCountUpPrevious)}
                      end={parseFloat(primaryEmissionPerSecondCountUp)}
                      thousandsSeparator={','}
                      duration={1}
                    />
                  ) : (
                    <></>
                  )}
                  <span style={{ color: '#15f2c9' }}> {` ${primaryEmissionPerSecond?.currency.symbol || ''}`}</span>
                  <span> / {emissionTimeframe === 'weekly' ? 'week' : 'day'}</span>
                </TYPE.body>
              </Heading>
              {secondaryEmissionPerSecond && secondaryEmissionPerSecond.greaterThan(0) && (
                <Heading>
                  <CurrencyLogoFromList currency={secondaryEmissionPerSecond.currency ?? undefined} size={'24px'} />
                  <TYPE.body fontWeight={500} margin={'5px'}>
                    {secondaryEmissionPerSecond?.multiply(multiplier)?.toFixed(0, { groupSeparator: ',' }) ?? '-'}
                    <span style={{ color: '#15f2c9' }}>{` ${secondaryEmissionPerSecond?.currency.symbol || ''}`}</span>
                    <span> / {emissionTimeframe === 'weekly' ? 'week' : 'day'}</span>
                  </TYPE.body>
                </Heading>
              )}
            </AutoColumn>
          </RewardRate>
        </RowBetween>
      </PoolData>
    </DataRow>
  )
}

const PoolHeading = styled(Box)<{ align?: string }>`
  text-align: ${({ align }) => align && align};
`

const RewardRate = styled(Box)<{ align?: string }>`
  text-align: ${({ align }) => align && align};
  margin-top: 7%;
`

const PoolData = styled(DataCard)`
  background: rgb(21, 242, 201, 0.01);
  border: 1px solid rgb(21, 242, 201, 0.08) !important;
  box-shadow: 0 0 15px 5px rgba(11, 13, 14, 0.85);
  padding: 1.5rem 5rem;
  z-index: 1;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    gap: 12px;
      padding: 1.5rem 1.5rem;
  `};
`

const Heading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`
